import React, { useMemo } from 'react';
import { Vault } from 'lucide-react';
import { SkeletonStatCards } from '../../../components/common/SkeletonLoader';
import { useAuth } from '../../../context/AuthContext';
import { useLineOfTrailKpi } from '../../../hooks/useLineOfTrailKpi';
import { previousMonth } from '../../../hooks/pipelineTracker/remContract';
import { Honesty, num } from './pipelineParts';
import { CHART_PALETTE, HousePanel } from './trackerKit';
import { dollars, monthShortYear } from './boardPackBullets';
import {
  NET_SETTLEMENTS_HEADLINE,
  buildNetBook,
  netSettlementsHeadlineCents,
  netSettlementsHeadlineLabel,
  type NetBook,
} from './netBook';
import { TargetGradeLine } from './targetGradeLine';
import type { TargetsDoc } from '../../../hooks/pipelineTracker/targetsContract';

/** '+$1,234' or '-$1,234', so a fall never reads as a balance. */
function signedDollars(cents: number): string {
  if (cents === 0) return dollars(0);
  return `${cents > 0 ? '+' : '-'}${dollars(Math.abs(cents))}`;
}

function signedCount(n: number): string {
  if (n === 0) return '0';
  return `${n > 0 ? '+' : '-'}${num(Math.abs(n))}`;
}

/**
 * The words for a NetBook that has no figure, or null when it has one. Exported for its tests; the
 * wording is what the board reads, so it is pinned.
 */
export function netBookNotice(book: NetBook): string | null {
  if (book.status === 'not_loaded') {
    return `Commission statements for ${monthShortYear(book.month)} are not loaded in VALUTTEN yet, so the loan book for the month cannot be shown.`;
  }
  if (book.status === 'incomplete') {
    return `${monthShortYear(book.month)} statements look part loaded: ${num(book.loans)} loans paid commission, against ${num(book.previousLoans)} in ${monthShortYear(book.previousMonth)}. `
      + 'The loan book is shown once the rest of the month\'s statements are in.';
  }
  return null;
}

/**
 * Net settlements: the loan book at the end of the pack month and its change over the month, from
 * the lender commission statements (netBook.ts). Rendered inside the Board pack's month section.
 *
 * THE TARGET. `targets` is the tenant's targets document once loaded (null while loading or when
 * it could not be loaded, and the panel then says nothing about a target). Its netSettlementsCents
 * is compared against the HEADLINE figure only, whichever NET_SETTLEMENTS_HEADLINE selects, and the
 * line names that figure so the target can never be read against the other one.
 */
export const NetBookPanel: React.FC<{ companyId: string; month: string; targets?: TargetsDoc | null }> = ({
  companyId,
  month,
  targets = null,
}) => {
  const { hideCommissions, showLoanTotals, isBrokerFamily } = useAuth();
  // The callable zeroes loan-book totals for an account that may not see them, which would read as
  // "not loaded". Say what is true instead, and do not ask.
  const hidden = hideCommissions && !showLoanTotals;
  const prev = previousMonth(month);
  const { series, loading, error } = useLineOfTrailKpi({
    companyId: hidden ? '' : companyId,
    // The dashboard's own Loan Book scope: company wide across every loan book. The server narrows
    // it to the caller's offices or own book exactly as it does on the dashboard.
    templateId: 'all',
    startMonth: hidden ? undefined : prev,
    endMonth: hidden ? undefined : month,
  });
  const book = useMemo(() => buildNetBook(series, month, prev), [series, month, prev]);
  const whose = isBrokerFamily ? 'Your loan book' : 'Loan book';

  const blurb = `${whose} at the end of ${monthShortYear(month)} from the lender commission statements in VALUTTEN, `
    + 'the same figure as the Loan Book on the dashboard. It is monthly, once each month\'s statements are loaded.';

  let body: React.ReactNode;
  if (hidden) {
    body = <Honesty>Loan book totals are hidden for your account.</Honesty>;
  } else if (loading) {
    body = <SkeletonStatCards count={2} columns={3} />;
  } else if (error) {
    body = <p className="text-sm text-red-300">The loan book could not be loaded from the commission statements.</p>;
  } else if (book.status !== 'ok') {
    body = <Honesty>{netBookNotice(book)}</Honesty>;
  } else {
    const closing = {
      label: `Loan book, end of ${monthShortYear(month)}`,
      value: dollars(book.closing.bookCents),
      sub: `${num(book.closing.loans)} loans paid commission in the month`,
      testId: 'net-book-closing',
    };
    const opening = 'bookCents' in book.opening ? book.opening : null;
    const change = book.change && opening
      ? {
          label: `Change over ${monthShortYear(month)}`,
          value: signedDollars(book.change.bookCents),
          sub: `${signedCount(book.change.loans)} loans paying commission, from ${dollars(opening.bookCents)} at the end of ${monthShortYear(opening.month)}`,
          testId: 'net-book-change',
        }
      : {
          label: `Change over ${monthShortYear(month)}`,
          value: 'Not available',
          sub: 'reason' in book.opening && book.opening.reason === 'incomplete'
            ? `${monthShortYear(book.opening.month)} statements look part loaded, so the change would not be a true figure`
            : `${monthShortYear(book.opening.month)} statements are not loaded, so there is nothing to compare with`,
          testId: 'net-book-change',
        };
    const [lead, second] = NET_SETTLEMENTS_HEADLINE === 'closing' ? [closing, change] : [change, closing];
    const headlineCents = netSettlementsHeadlineCents(book);
    body = (
      <div className="space-y-3">
        <div data-testid={lead.testId} data-headline="true">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-400">{lead.label}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-100">{lead.value}</p>
          <p className="mt-0.5 text-xs text-gray-400">{lead.sub}</p>
          {targets && (
            <div className="mt-1.5" data-testid="net-book-target">
              <TargetGradeLine
                target={targets.netSettlementsCents}
                actual={headlineCents}
                rag={targets.rag}
                format={dollars}
                ungradedReason={`the ${netSettlementsHeadlineLabel()} is not available for this month`}
                noTargetText={`No net settlements target set (set against the ${netSettlementsHeadlineLabel()})`}
              />
            </div>
          )}
        </div>
        <div data-testid={second.testId} className="border-t border-gray-700/70 pt-3">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-400">{second.label}</p>
          <p className="mt-1 text-base font-semibold tabular-nums text-gray-200">{second.value}</p>
          <p className="mt-0.5 text-xs text-gray-400">{second.sub}</p>
        </div>
      </div>
    );
  }

  return (
    <HousePanel title="Net settlements" icon={Vault} color={CHART_PALETTE[4]} blurb={blurb}>
      <div data-testid="net-book">{body}</div>
    </HousePanel>
  );
};
